import type { Order } from "@/lib/projects";
import type { ProjectSchadensmeldung, ProjectTimesheet } from "@/lib/projectOverview";
import { isFixedPriceOrderType } from "@/pages/projekte/hoursUsage";

function formatDateShort(value: string | null | undefined): string {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
}

export function formatTimesheetPeriod(ts: ProjectTimesheet): string {
  const from = formatDateShort(ts.date_from as string | undefined ?? ts.period_start);
  const to = formatDateShort(ts.date_to as string | undefined ?? ts.period_end);
  if (from && to) return from === to ? from : `${from} – ${to}`;
  return from || to || "";
}

function normalizeTimesheetStatus(status: string | null | undefined): string {
  const key = String(status || "draft").toLowerCase().trim();
  if (key === "ready_for_signature") return "ready";
  return key || "draft";
}

export function isSupersededTimesheet(ts: ProjectTimesheet): boolean {
  return Boolean(ts.has_newer_revision);
}

export function translateTimesheetStatus(status: string | null | undefined): string {
  switch (normalizeTimesheetStatus(status)) {
    case "draft":
      return "Entwurf";
    case "ready":
      return "Bereit für Unterschrift";
    case "signed":
      return "Unterschrieben";
    case "invoice_draft":
      return "Rechnungsentwurf erstellt";
    case "invoice_created":
      return "Abgerechnet";
    case "invoice_paid":
      return "Bezahlt";
    case "archived":
      return "Archiviert";
    default:
      return String(status || "");
  }
}

export function getTimesheetRowTitle(ts: ProjectTimesheet): string {
  const number =
    ts.display_number ||
    (ts.timesheet_number != null ? String(ts.timesheet_number) : "") ||
    (ts.id != null ? String(ts.id) : "");
  return number ? `Stundennachweis Nr. ${number}` : "Stundennachweis";
}

export function getTimesheetPublicLink(ts: ProjectTimesheet): string | null {
  const links = ts.public_links as { draft_url?: string; signed_url?: string } | undefined;
  if (links?.draft_url || links?.signed_url) {
    const status = normalizeTimesheetStatus(ts.status_display || ts.status);
    if (
      status === "signed" ||
      status === "invoice_draft" ||
      status === "invoice_created" ||
      status === "invoice_paid"
    ) {
      return links.signed_url || links.draft_url || null;
    }
    return links.draft_url || links.signed_url || null;
  }
  const token = ts.public_token;
  if (!token) return null;
  const portal =
    typeof location !== "undefined" && location.hostname.startsWith("dev.")
      ? "https://dev.portal.ernsthaus.de"
      : "https://portal.ernsthaus.de";
  return `${portal}/public/timesheet/${encodeURIComponent(String(token))}`;
}

export function isFestpreisProject(
  orderTypeValue: unknown,
  strictFromSchemas?: boolean,
): boolean {
  return strictFromSchemas ?? isFixedPriceOrderType(orderTypeValue);
}

export function getCompletionPdfUrl(project: Order): string | null {
  const url = project.project_completion_pdf_share_url;
  return url ? String(url).trim() : null;
}

export function getMuendlichBeauftragtPdfUrl(project: Order): string | null {
  const url = project.muendlich_beauftragt_pdf_share_url;
  return url ? String(url).trim() : null;
}

export type OverviewEvidenceItem =
  | { kind: "timesheet"; ts: ProjectTimesheet; sortMs: number }
  | { kind: "completion"; project: Order; sortMs: number }
  | { kind: "muendlichBeauftragt"; project: Order; sortMs: number }
  | { kind: "schadensmeldung"; item: ProjectSchadensmeldung; sortMs: number };

export function buildOverviewEvidenceList(
  project: Order,
  timesheets: ProjectTimesheet[],
  schadensmeldungen: ProjectSchadensmeldung[] = [],
): OverviewEvidenceItem[] {
  const items: OverviewEvidenceItem[] = [];
  for (const ts of timesheets) {
    const raw =
      ts.date_to ||
      ts.period_end ||
      (ts.updated_at as string | undefined) ||
      (ts.created_at as string | undefined) ||
      ts.date_from ||
      ts.period_start ||
      "";
    const ms = raw ? Date.parse(String(raw)) : 0;
    items.push({ kind: "timesheet", ts, sortMs: Number.isFinite(ms) ? ms : 0 });
  }
  // Nur echte unterschriebene Fertigstellungsmeldungen (mit PDF).
  // Stundennachweis-/Lieferschein-Abschlüsse setzen kein PDF und gehören nicht hierher.
  if (project.project_completion_created_at && getCompletionPdfUrl(project)) {
    const ms = Date.parse(String(project.project_completion_created_at));
    items.push({
      kind: "completion",
      project,
      sortMs: Number.isFinite(ms) ? ms : 0,
    });
  }
  if (project.muendlich_beauftragt_at) {
    const ms = Date.parse(String(project.muendlich_beauftragt_at));
    items.push({
      kind: "muendlichBeauftragt",
      project,
      sortMs: Number.isFinite(ms) ? ms : 0,
    });
  }
  for (const sm of schadensmeldungen) {
    const ms = sm.created_at ? Date.parse(String(sm.created_at)) : 0;
    items.push({
      kind: "schadensmeldung",
      item: sm,
      sortMs: Number.isFinite(ms) ? ms : 0,
    });
  }
  items.sort((a, b) => b.sortMs - a.sortMs);
  return items;
}
